import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { ShieldCheck, DeviceMobile, Desktop, DeviceTablet, X, Clock } from "@phosphor-icons/react";

function DeviceIcon({ device }) {
    if (device === "mobile") return <DeviceMobile size={20} weight="duotone" />;
    if (device === "tablet") return <DeviceTablet size={20} weight="duotone" />;
    return <Desktop size={20} weight="duotone" />;
}

function useCountdown(target) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    const ms = new Date(target).getTime() - now;
    if (isNaN(ms) || ms <= 0) return "expired";
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    if (h > 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
    return `${h}h ${m.toString().padStart(2, "0")}m ${s.toString().padStart(2, "0")}s`;
}

function SessionRow({ s, onRevoke }) {
    const left = useCountdown(s.expires_at);
    return (
        <li className="py-3 flex items-center justify-between gap-4" data-testid={`session-row-${s.id}`}>
            <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 bg-secondary rounded-sm flex items-center justify-center shrink-0">
                    <DeviceIcon device={s.device} />
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                        {s.browser || "Browser"} · {s.device || "device"} ·
                        <span className="uppercase text-[10px] tracking-widest px-1.5 py-0.5 bg-secondary rounded-sm ms-1">{s.provider}</span>
                        {s.country && <span className="ms-1 text-xs text-muted-foreground">· {s.country}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mono truncate">IP {s.ip} · created {new Date(s.created_at).toLocaleString()}</div>
                    <div className="text-xs mt-1 inline-flex items-center gap-1 text-primary font-medium" data-testid={`session-ttl-${s.id}`}>
                        <Clock size={12} weight="bold" /> Expires in {left}
                    </div>
                </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onRevoke(s.id)} data-testid={`session-revoke-${s.id}`}>
                <X size={14} weight="bold" className="me-1" />Revoke
            </Button>
        </li>
    );
}

function ShiftPinInline() {
    const [pin, setPin] = useState("");
    const [busy, setBusy] = useState(false);
    const save = async () => {
        if (!/^\d{6}$/.test(pin)) { toast.error("PIN must be 6 digits"); return; }
        setBusy(true);
        try {
            await api.post("/me/shift-pin", { pin });
            toast.success("Shift PIN saved");
            setPin("");
        } catch { toast.error("Could not save PIN"); }
        setBusy(false);
    };
    return (
        <div className="flex items-center gap-2 max-w-sm">
            <Input inputMode="numeric" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6 digits" className="mono text-2xl text-center tracking-[0.5em]" data-testid="shift-pin-input" />
            <Button onClick={save} disabled={busy || pin.length !== 6} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="shift-pin-save">Save</Button>
        </div>
    );
}

export default function Security() {
    const { user, refresh } = useAuth();
    const { t } = useI18n();
    const [sessions, setSessions] = useState([]);
    const [mfaSetup, setMfaSetup] = useState(null);
    const [mfaCode, setMfaCode] = useState("");
    const [ttl, setTtl] = useState(user?.session_ttl_hours || "");
    const [savingTtl, setSavingTtl] = useState(false);

    const load = () => api.get("/sessions/my").then((r) => setSessions(r.data.sessions));
    useEffect(() => { load(); }, []);
    useEffect(() => { setTtl(user?.session_ttl_hours || ""); }, [user]);

    const saveTtl = async () => {
        setSavingTtl(true);
        try {
            if (ttl === "" || ttl === null) {
                await api.patch("/me/prefs", {});
                toast.success("Preference cleared — provider default applies");
            } else {
                const n = parseInt(ttl, 10);
                if (!Number.isFinite(n) || n < 1 || n > 168) {
                    toast.error("TTL must be 1–168 hours");
                    setSavingTtl(false);
                    return;
                }
                await api.patch("/me/prefs", { session_ttl_hours: n });
                toast.success(`Future sessions expire after ${n}h`);
            }
            await refresh();
        } catch (e) { toast.error(t("error")); }
        finally { setSavingTtl(false); }
    };
    const startMfa = async () => { const { data } = await api.post("/auth/mfa/setup"); setMfaSetup(data); };
    const enableMfa = async () => {
        try {
            await api.post("/auth/mfa/enable", { code: mfaCode });
            toast.success("MFA enabled");
            setMfaSetup(null); setMfaCode("");
            await refresh();
        } catch { toast.error("Invalid code"); }
    };
    const disableMfa = async () => { await api.post("/auth/mfa/disable"); toast.success("MFA disabled"); await refresh(); };
    const revoke = async (id) => {
        try { await api.delete(`/sessions/${id}`); toast.success("Session revoked"); load(); }
        catch { toast.error(t("error")); }
    };

    return (
        <div className="space-y-8 max-w-4xl" data-testid="security-page">
            <div>
                <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· PERSONAL SECURITY</div>
                <h1 className="text-3xl font-bold tracking-tight mt-1">{t("security")}</h1>
                <p className="text-sm text-muted-foreground mt-1">Two-factor authentication, session lifetime, and active sign-in sessions.</p>
            </div>

            <Card className="p-6 rounded-sm border-border">
                <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3">{t("adminMfaTitle")}</div>
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="text-sm">{t("status")}: <b>{user?.mfa_enabled ? "ENABLED" : "DISABLED"}</b></div>
                    {user?.mfa_enabled ? (
                        <Button variant="outline" onClick={disableMfa} data-testid="mfa-disable">{t("adminDisableMfa")}</Button>
                    ) : (
                        <Button onClick={startMfa} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="mfa-setup"><ShieldCheck size={16} weight="fill" className="me-1" />{t("adminEnableMfa")}</Button>
                    )}
                </div>
            </Card>

            <Card className="p-6 rounded-sm border-border">
                <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3">{t("adminSessionLifetime")}</div>
                <p className="text-sm text-muted-foreground mb-3">
                    Cap the lifetime of any new session (credentials or Google) to match your shift. Blank uses the provider default (8h credentials, 7d Google).
                </p>
                <div className="flex items-center gap-2 max-w-sm">
                    <Input type="number" min={1} max={168} value={ttl} onChange={(e) => setTtl(e.target.value)} placeholder="1 – 168 hours" data-testid="ttl-input" />
                    <Button onClick={saveTtl} disabled={savingTtl} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="ttl-save">{t("save")}</Button>
                </div>
                {user?.session_ttl_hours && (
                    <div className="mt-3 text-xs mono text-muted-foreground">Current cap: <b>{user.session_ttl_hours}h</b></div>
                )}
            </Card>

            <Card className="p-6 rounded-sm border-border">
                <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3">{t("adminShiftPin")}</div>
                <p className="text-sm text-muted-foreground mb-3">
                    Set a 6-digit PIN so an outgoing colleague can hand the scanner over to you without a full re-login. Store it privately.
                </p>
                <ShiftPinInline />
            </Card>

            <Card className="p-6 rounded-sm border-border">
                <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3">{t("adminActiveSessions")}</div>
                {sessions.length === 0 && <div className="text-sm text-muted-foreground">{t("adminNoSessions")}.</div>}
                <ul className="divide-y divide-border">
                    {sessions.map((s) => (
                        <SessionRow key={s.id} s={s} onRevoke={revoke} />
                    ))}
                </ul>
            </Card>

            <Dialog open={!!mfaSetup} onOpenChange={(o) => !o && setMfaSetup(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{t("adminEnableMfa")}</DialogTitle></DialogHeader>
                    {mfaSetup && (
                        <div className="space-y-4 text-sm">
                            <p>Scan this QR with Google/Microsoft Authenticator:</p>
                            <div className="flex justify-center bg-white p-4 rounded-sm">
                                <QRCodeCanvas value={mfaSetup.otpauth_url} size={180} level="M" />
                            </div>
                            <div className="mono text-xs break-all bg-secondary/40 p-2 rounded-sm">{mfaSetup.secret}</div>
                            <Label>{t("adminVerificationCode")}</Label>
                            <Input value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} maxLength={6} className="mono text-2xl text-center tracking-widest" data-testid="mfa-code-input" />
                            <Button onClick={enableMfa} className="w-full bg-primary hover:bg-primary/90 rounded-sm" data-testid="mfa-enable-confirm">{t("adminEnable")}</Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
